import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, resolveOnboardingPath } from "../../contexts/AuthContext";
import { useSignup } from "../../contexts/SignupContext";
import { authService } from "../../services/auth.service";
import { startOAuthPopup } from "../../utils/oauthClient";

import styles from "../../styles/pages/auth/LoginScreen.module.css";
import ChoiceChip from "../../ui/selectors/ChoiceChip";

import googleLogo from "../../assets/icons/google.svg";
import microsoftLogo from "../../assets/icons/microsoft.svg";
import alertSVG from "../../assets/icons/alert.svg";

import Button from "../../ui/buttons/Button";
import BaseInput from "../../ui/inputs/BaseInput/BaseInput";
import CountryPicker from "../../ui/inputs/CountryPicker";
import { PHONE_COUNTRY_OPTIONS } from "../../utils/countryOptions";

const EU_COUNTRY_CODES = new Set([
  "AT", "BE", "BG", "CY", "CZ", "DE", "DK", "EE", "ES", "FI", "FR", "GR", "HR", "HU", "IE", "IT", "LT", "LU",
  "LV", "MT", "NL", "PL", "PT", "RO", "SE", "SI", "SK",
]);

const hasSeparateSharingConsent = (countryCode) => countryCode === "BR" || EU_COUNTRY_CODES.has(countryCode);

const OAUTH_PROVIDERS = [
  { key: "microsoft", name: "Microsoft", logo: microsoftLogo },
  { key: "google", name: "Google", logo: googleLogo },
];

export default function LoginScreen() {
  const { loginWithPassword, loginWithTokens } = useAuth();
  const { setEmail: setSignupEmail, setPassword: setSignupPassword } = useSignup();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("login");

  // Login state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [loginError, setLoginError] = useState("");

  // Register state
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [registerConfirmPassword, setRegisterConfirmPassword] = useState("");
  const [registerCountry, setRegisterCountry] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [shareContacts, setShareContacts] = useState(false);
  const [registerFieldErrors, setRegisterFieldErrors] = useState({});
  const [loadingRegister, setLoadingRegister] = useState(false);

  // OAuth state
  const [loadingOAuth, setLoadingOAuth] = useState(null);

  const popupRef = useRef(null);
  const popupWatchRef = useRef(null);

  const clearOAuthState = useCallback(() => {
    if (popupWatchRef.current) {
      clearInterval(popupWatchRef.current);
      popupWatchRef.current = null;
    }
    try {
      popupRef.current?.close();
    } catch { }
    popupRef.current = null;
    setLoadingOAuth(null);
  }, []);

  useEffect(() => {
    const onMessage = async (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || typeof data !== "object") return;

      if (data.type === "oauth_error") {
        console.error("OAuth error:", data);
        clearOAuthState();
        return;
      }

      if (data.type === "oauth_success") {
        const { access, refresh } = data;
        if (!access || !refresh) {
          console.error("OAuth success sin tokens:", data);
          clearOAuthState();
          return;
        }
        clearOAuthState();
        try {
          const nextAuth = await loginWithTokens({ access, refresh });
          navigate(resolveOnboardingPath(nextAuth) || "/dashboard", {
            replace: true,
          });
        } catch (err) {
          console.error("Error post-OAuth login:", err?.response?.data || err);
          setLoginError("Se inició sesión, pero falló la carga del perfil.");
        }
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [clearOAuthState, loginWithTokens, navigate]);

  const handleOAuthLogin = async (provider) => {
    setLoginError("");
    setLoadingOAuth(provider);

    try {
      const flow = activeTab === "register" ? "signup" : "login";
      let signupData;
      if (flow === "signup") {
        const errors = validateSignupConsents();
        if (Object.keys(errors).length) {
          setRegisterFieldErrors(errors);
          setLoadingOAuth(null);
          return;
        }
        signupData = {
          country_code: registerCountry,
          accept_terms: true,
          share_contacts: hasSeparateSharingConsent(registerCountry) ? shareContacts : true,
        };
      }
      popupRef.current = await startOAuthPopup({
        provider,
        flow,
        title: `${flow} ${provider}`,
        signupData,
      });

      const startedAt = Date.now();
      popupWatchRef.current = setInterval(() => {
        try {
          if (!popupRef.current || popupRef.current.closed) {
            clearOAuthState();
            return;
          }
          if (Date.now() - startedAt > 90_000) {
            clearOAuthState();
          }
        } catch {
          clearOAuthState();
        }
      }, 400);
    } catch (err) {
      console.error(err);
      setLoginError("No se pudo iniciar sesión con OAuth");
      setLoadingOAuth(null);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setLoginError("");
    setRegisterFieldErrors({});
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    setLoginError("");

    if (!loginEmail || !loginPassword) {
      setLoginError("Por favor, complete todos los campos.");
      return;
    }

    setLoadingLogin(true);
    try {
      const nextAuth = await loginWithPassword({
        username: loginEmail,
        password: loginPassword,
      });
      navigate(resolveOnboardingPath(nextAuth) || "/dashboard", {
        replace: true,
      });
    } catch (err) {
      const status = err?.response?.status;
      const code = err?.response?.data?.code;

      if (status === 403 && code === "EMAIL_NOT_VERIFIED") {
        setSignupEmail(loginEmail);
        navigate("/signup/verify-email");
      } else if (status >= 500) {
        setLoginError("Ocurrió un error del servidor. Inténtalo más tarde.");
      } else {
        setLoginError("Usuario o contraseña incorrectos");
      }
      console.error(err?.response?.data || err);
    } finally {
      setLoadingLogin(false);
    }
  };

  const validateRegisterForm = () => {
    const errors = {};

    if (!registerEmail) {
      errors.email = "El email es requerido";
    }

    if (!registerPassword) {
      errors.password = "La contraseña es requerida";
    } else if (registerPassword.length < 8) {
      errors.password = "La contraseña debe tener al menos 8 caracteres";
    }

    if (!registerConfirmPassword) {
      errors.confirmPassword = "Por favor, confirme su contraseña";
    } else if (registerPassword !== registerConfirmPassword) {
      errors.confirmPassword = "Las contraseñas no coinciden";
    }

    Object.assign(errors, validateSignupConsents());

    return errors;
  };

  const validateSignupConsents = () => {
    const errors = {};
    if (!registerCountry) errors.country = "Seleccioná tu país";
    if (!acceptTerms) errors.acceptTerms = "Debes aceptar los documentos legales";
    return errors;
  };

  const handleRegisterSubmit = async (event) => {
    event.preventDefault();
    const errors = validateRegisterForm();
    if (Object.keys(errors).length > 0) {
      setRegisterFieldErrors(errors);
      return;
    }

    setLoadingRegister(true);
    try {
      // 201 REGISTERED o 200 EMAIL_NOT_VERIFIED (reenvío) resuelven OK.
      await authService.register({
        email: registerEmail,
        password: registerPassword,
        password_confirm: registerConfirmPassword,
        country_code: registerCountry,
        accept_terms: acceptTerms,
        share_contacts: hasSeparateSharingConsent(registerCountry) ? shareContacts : true,
      });

      setSignupEmail(registerEmail);
      setSignupPassword(registerPassword);
      navigate("/signup/verify-email");
    } catch (err) {
      const status = err?.response?.status;
      const code = err?.response?.data?.code;
      const data = err?.response?.data || {};

      const firstError = (value) =>
        Array.isArray(value) ? value[0] : typeof value === "string" ? value : "";

      if (status === 409 || code === "EMAIL_ALREADY_REGISTERED") {
        setActiveTab("login");
        setLoginError("Ese email ya está registrado. Iniciá sesión.");
      } else if (status === 400) {
        setRegisterFieldErrors({
          email: firstError(data.email),
          password:
            firstError(data.password) ||
            firstError(data.password_confirm) ||
            firstError(data.non_field_errors),
        });
      } else {
        setRegisterFieldErrors({
          email: "No se pudo completar el registro. Intentá nuevamente.",
        });
      }
      console.error(err?.response?.data || err);
    } finally {
      setLoadingRegister(false);
    }
  };

  const clearRegisterFieldError = (field) => {
    if (registerFieldErrors[field]) {
      setRegisterFieldErrors((previous) => ({ ...previous, [field]: "" }));
    }
  };

  const oauthActionPrefix =
    activeTab === "register" ? "Registrarse con" : "Iniciar sesión con";

  const oauthSection = (
    <>
      <div className={styles.divider}>
        <span>O</span>
      </div>
      <div className={styles.oAuthContainer}>
        {OAUTH_PROVIDERS.map((provider) => (
          <button
            key={provider.key}
            type="button"
            onClick={() => handleOAuthLogin(provider.key)}
            className={styles.loginOauthButton}
          >
            <img src={provider.logo} alt="" aria-hidden="true" />
            {loadingOAuth === provider.key
              ? "Conectando..."
              : `${oauthActionPrefix} ${provider.name}`}
          </button>
        ))}
      </div>
    </>
  );

  return (
    <div className={styles.login}>
      <div className={styles.leftLogin} />

      <div className={styles.rightLogin}>
        <h1 className={styles.loginTitle}>
          Te damos la bienvenida a
          <br />
          Maqmailing
        </h1>

        <div className={styles.filterGroup}>
          <ChoiceChip
            label="Iniciar Sesión"
            selected={activeTab === "login"}
            onClick={() => handleTabChange("login")}
            height={40}
            iconSize={0}
            fontWeight={500}
            padding="12px 16px"
            selectedColor="var(--text-cta-primary)"
            unselectedColor="var(--text-tertiary)"
            selectedBackground="var(--surface-accent-hover)"
            unselectedBackground="transparent"
          />
          <ChoiceChip
            label="Crear una cuenta"
            selected={activeTab === "register"}
            onClick={() => handleTabChange("register")}
            height={40}
            iconSize={0}
            fontWeight={500}
            padding="12px 16px"
            selectedColor="var(--text-cta-primary)"
            unselectedColor="var(--text-tertiary)"
            selectedBackground="var(--surface-accent-hover)"
            unselectedBackground="transparent"
          />
        </div>

        {activeTab === "login" ? (
          <form onSubmit={handleLoginSubmit} className={styles.formContent}>
            <BaseInput
              type="email"
              label="Email"
              placeholder="Email"
              value={loginEmail}
              onChange={(value) => setLoginEmail(value)}
              validateOnChange={false}
            />
            <BaseInput
              type="password"
              label="Contraseña"
              placeholder="Contraseña"
              value={loginPassword}
              onChange={(value) => setLoginPassword(value)}
              validateOnChange={false}
            />
            {loginError && (
              <div className={styles.loginError}>
                <img src={alertSVG} alt="" aria-hidden="true" />
                {loginError}
              </div>
            )}
            <Link to="/recover-password" className={styles.loginForgotPassword}>
              Olvidé mi contraseña
            </Link>
            <Button
              type="submit"
              style={{
                backgroundColor: `var(${loadingLogin ? "--color-brand-600" : "--color-brand-500"})`,
                minHeight: 45,
              }}
              loading={loadingLogin}
            >
              {loadingLogin ? "Iniciando sesión..." : "Iniciar sesión"}
            </Button>
            {oauthSection}
          </form>
        ) : (
          <form onSubmit={handleRegisterSubmit} className={styles.formContent}>
            <label className={styles.signupField}>
              <span>País</span>
              <CountryPicker
                options={PHONE_COUNTRY_OPTIONS}
                value={registerCountry}
                onChange={(option) => {
                  setRegisterCountry(option?.code ?? "");
                  setShareContacts(false);
                  setRegisterFieldErrors((current) => ({ ...current, country: "" }));
                }}
                placeholder="Seleccioná tu país"
                variant="field"
                ariaInvalid={Boolean(registerFieldErrors.country)}
                ariaRequired
              />
              {registerFieldErrors.country && <span className={styles.consentError}>{registerFieldErrors.country}</span>}
            </label>
            <BaseInput
              type="email"
              label="Email"
              placeholder="Email"
              value={registerEmail}
              onChange={(value) => {
                setRegisterEmail(value);
                clearRegisterFieldError("email");
              }}
              error={registerFieldErrors.email}
              validateOnChange={false}
            />
            <BaseInput
              type="password"
              label="Contraseña"
              placeholder="Contraseña"
              value={registerPassword}
              onChange={(value) => {
                setRegisterPassword(value);
                clearRegisterFieldError("password");
              }}
              error={registerFieldErrors.password}
              validateOnChange={false}
            />
            <label className={styles.consentLabel}>
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(event) => {
                  setAcceptTerms(event.target.checked);
                  setRegisterFieldErrors((current) => ({ ...current, acceptTerms: "" }));
                }}
                required
              />
              <span>
                Acepto los <a href="https://maqmailing.io/legal/terminos-de-suscripcion" target="_blank" rel="noreferrer">Términos de Suscripción</a>, las <a href="https://maqmailing.io/legal/condiciones-del-servicio" target="_blank" rel="noreferrer">Condiciones del Servicio</a> y la <a href="https://maqmailing.io/legal/politica-de-privacidad" target="_blank" rel="noreferrer">Política de Privacidad</a>
                {hasSeparateSharingConsent(registerCountry) ? "." : <>, incluida <a href="https://maqmailing.io/legal/terminos-de-suscripcion#clause-5" target="_blank" rel="noreferrer">la red de contactos verificados (cláusula 5)</a>.</>}
              </span>
            </label>
            {registerFieldErrors.acceptTerms && <span className={styles.consentError}>{registerFieldErrors.acceptTerms}</span>}
            {hasSeparateSharingConsent(registerCountry) && (
              <label className={styles.consentLabel}>
                <input
                  type="checkbox"
                  checked={shareContacts}
                  onChange={(event) => setShareContacts(event.target.checked)}
                />
                <span>Quiero compartir mis contactos verificados con la red de MaqMailing.</span>
              </label>
            )}
            <BaseInput
              type="password"
              label="Confirmar Contraseña"
              placeholder="Confirmar Contraseña"
              value={registerConfirmPassword}
              onChange={(value) => {
                setRegisterConfirmPassword(value);
                clearRegisterFieldError("confirmPassword");
              }}
              error={registerFieldErrors.confirmPassword}
              validateOnChange={false}
            />
            <Button
              type="submit"
              style={{
                backgroundColor: "var(--color-brand-500)",
                minHeight: 45,
              }}
              loading={loadingRegister}
            >
              {loadingRegister ? "Creando tu cuenta..." : "Continuar a elegir tu plan"}
            </Button>
            {oauthSection}
          </form>
        )}
      </div>
    </div>
  );
}
